import { BonusStage } from '../services/user/data/user-bonuses.data';
import { ActionRoutesConfig } from '../types/promo-shared.type';
import { PromoActionsEnum } from '../enums/promo-actions.enum';
import { CookieService, TimeService } from 'ngx-unificator/services';
import { PromoTemplateEnum } from '../enums/promo-template.enum';
import { CurrencyService } from '../services/currency.service';
import { BonusesStatuses, GroupsService } from '../services/groups.service';
import { CurrentBonusItem, OnlyActivePromoPages } from '../types/promo.type';

export const ALREADY_SPINED_SLOT_STR = 'already_spined_slot';

export enum EventType {
  First = 'first',
  Second = 'second',
  Third = 'third',
}

export function buildPromoActionRoutesConfig(overrides: {
  gamesUrl: string;
  promoAuthUrl?: string;
  huntUrl?: string;
}): ActionRoutesConfig<typeof PromoActionsEnum> {
  return {
    [PromoActionsEnum.GET_BONUS]: {
      key: null,
      url: '/profile/account',
      authUrl: ['profile', 'account'],
    },
    [PromoActionsEnum.GO_TO_BONUSES]: {
      key: null,
      url: '/bonuses',
      authUrl: ['bonuses'],
    },
    [PromoActionsEnum.TOURNAMENTS]: {
      key: 'promo-tournaments',
      url: '/tournaments',
      authUrl: ['tournaments'],
    },
    [PromoActionsEnum.GO_TO_DEPOSIT]: {
      key: 'promo-deposit',
      url: '/account/deposit',
      authUrl: ['account', 'deposit'],
    },
    [PromoActionsEnum.HUNT_DEPOSIT]: {
      key: 'promo-deposit',
      url: '/profile/deposit',
      authUrl: ['profile', 'deposit'],
    },

    // overrides
    [PromoActionsEnum.PROMO_AUTH]: {
      key: 'promo-auth',
      url: overrides.promoAuthUrl,
      authUrl: null,
    },
    [PromoActionsEnum.GAMES]: {
      key: null,
      url: overrides.gamesUrl,
      authUrl: null,
    },
    [PromoActionsEnum.HUNT]: {
      key: null,
      url: overrides.huntUrl,
      authUrl: null,
    },
  };
}

export function getFilteredCashWelcomeBonuses(arrayToCheck: any[], cookieService: CookieService): any {
  return arrayToCheck
    ?.filter((bonus) => bonus.stage === BonusStage.HANDLE_BETS && bonus.isWelcome && !cookieService.check(ALREADY_SPINED_SLOT_STR))
    .sort((a, b) => b.created_at - a.created_at)[0];
}

export function getFilteredFreespinsWelcomeBonuses(arrayToCheck: any[], cookieService: CookieService): any {
  return arrayToCheck
    ?.filter((bonus) => bonus.stage === BonusStage.ISSUED && bonus.isWelcome && !cookieService.check(ALREADY_SPINED_SLOT_STR))
    .sort((a, b) => b.created_at - a.created_at)[0];
}

export function getFilteredWelcomeLootbox(arrayToCheck: any[]): any {
  return arrayToCheck?.filter((lootbox) => lootbox.isWelcome && lootbox.stage === BonusStage.ISSUED)[0];
}

export function checkActivatedTodayLootbox(lootboxes: any[], bonusIds: string[], timeService: TimeService): boolean {
  if (!lootboxes?.length || !bonusIds?.length) return false;

  return bonusIds.every((bonusId) =>
    lootboxes.some(
      (lootbox) =>
        lootbox?.stage === BonusStage.ACTIVATED &&
        timeService.isTodayDate(lootbox?.created_at) &&
        (lootbox?.lootboxTitle === bonusId || lootbox?.title === bonusId),
    ),
  );
}

export function getFilteredPromoLootbox(arrayToCheck: any[], bonusIds: string[], stage: BonusStage): any {
  return arrayToCheck
    ?.filter((lootbox) => bonusIds.includes(lootbox.lootboxTitle) && lootbox.stage === stage)
    .sort((a, b) => b.created_at - a.created_at)[0];
}

export function prepareBonusIds(bonusIds?: string): string[] {
  if (!bonusIds) return [];

  return bonusIds
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
}

export function mapPromoBonusTitle(title: string): string {
  return (title || '')
    .replace(/\[(.*?)]/g, '')
    .replace(/\[(.*?)}/g, '')
    .replace(/\{(.*?)]/g, '')
    .replace(/\{(.*?)}/g, '');
}

export function replaceStringInPageFields(value: any, replaceString: string, replaceResult: string): any {
  if (typeof value === 'string') {
    return value.replace(new RegExp(replaceString, 'g'), replaceResult);
  }

  if (Array.isArray(value)) {
    return value.map((item) => replaceStringInPageFields(item, replaceString, replaceResult));
  }

  if (!value || typeof value !== 'object' || value instanceof Date) {
    return value;
  }

  return Object.entries(value).reduce(
    (acc, [key, item]) => ({
      ...acc,
      [key]: replaceStringInPageFields(item, replaceString, replaceResult),
    }),
    {},
  );
}

export function resolveActivePromo(list: OnlyActivePromoPages[]): OnlyActivePromoPages {
  return list.find((item) => item.template_slug !== PromoTemplateEnum.NON_ACTIVE);
}

export function resolveCurrentPromoBonus(lootboxes: any[], freespins: any[], bonuses: any[], bonusIds: string, cookieService: CookieService): CurrentBonusItem {
  const preparedBonusIds = prepareBonusIds(bonusIds);
  const cash = getFilteredCashWelcomeBonuses(bonuses, cookieService);
  const fs = getFilteredFreespinsWelcomeBonuses(freespins, cookieService);
  const welcomeLootbox = getFilteredWelcomeLootbox(lootboxes);
  const promoIssuedLootbox = getFilteredPromoLootbox(lootboxes, preparedBonusIds, BonusStage.ISSUED);

  return (fs && cash) || (welcomeLootbox && cash) || fs || cash || promoIssuedLootbox
    ? {
        cash,
        fs,
        welcomeLootbox,
        promoIssuedLootbox,
      }
    : null;
}

export function resolvePromoParticipationState(currentBonus: CurrentBonusItem, groupsService: GroupsService, isUserAlreadyActivatedPromoLootbox: boolean) {
  const promoIssuedLootbox = currentBonus?.promoIssuedLootbox;
  const cash = currentBonus?.cash;
  const fs = currentBonus?.fs;
  const welcomeLootbox = currentBonus?.welcomeLootbox;
  const isAllWelcomePackClaimed =
    groupsService.isExistGroup(BonusesStatuses.ALL_WELCOME_OFFERS_USED) || groupsService.isExistGroup(BonusesStatuses.ALL_SPECIAL_WELCOME_USED);

  return {
    isUserCanParticipateInPromo: Boolean(isAllWelcomePackClaimed && promoIssuedLootbox),
    isUserCanPartiallyParticipateInPromo: Boolean((cash || fs || welcomeLootbox) && !promoIssuedLootbox),
    isUserAlreadyActivatedPromoLootbox,
    showTryTommorowOnDestroy: isUserAlreadyActivatedPromoLootbox,
  };
}

export function resolveBoxBonuses(cmsBonuses: any[], lootboxes: any[], ids: string[], timeService: TimeService) {
  const bonuses = cmsBonuses.map((promo, index) => {
    const id = ids[index];
    const matchingLootbox = lootboxes.find((lootbox) => lootbox?.title === id || lootbox?.lootboxTitle === id);
    const isTaken = [BonusStage.ISSUED, BonusStage.ACTIVATED].includes(matchingLootbox?.stage) && timeService.isTodayDate(matchingLootbox?.created_at);

    return {
      ...promo,
      isTaken,
    };
  });

  return {
    bonuses,
    isFirstTaken: bonuses[0]?.isTaken,
    isSecondTaken: bonuses[1]?.isTaken,
    isThirdTaken: bonuses[2]?.isTaken,
    isAllTaken: bonuses.every((bonus) => bonus.isTaken),
  };
}

export function resolveSlotLevelsBonuses(cmsBonuses: any[], lootboxes: any[], ids: string[], timeService: TimeService) {
  return cmsBonuses
    .map((level: any) => ({
      ...level,
      fsList: level.fsList.split(',').map((fs: string) => fs.trim()),
    }))
    .map((promo, index) => {
      const id = ids[index];
      const matchingLootbox = lootboxes.find((lootbox) => lootbox?.lootboxTitle === id);
      const isTaken = [BonusStage.ISSUED, BonusStage.ACTIVATED].includes(matchingLootbox?.stage) && timeService.isTodayDate(matchingLootbox?.created_at);

      return {
        ...promo,
        isTaken,
      };
    });
}

export function replaceGoalAmountByCurrency(page: any, currency: string, currencyService: CurrencyService): any {
  const goalAmount = page?.AmountGoalByCurrency?.[currency];
  const currencySymbol = currencyService.currencySymbol(currency);
  const formattedGoalAmount = goalAmount != null ? `${currencySymbol}${Number(goalAmount)}` : '';

  return replaceStringInPageFields(page, '#goalAmount#', formattedGoalAmount);
}

export function getTodaySuccessfulDeposits(transactions: any[], timeService: TimeService): any[] {
  return (transactions || []).filter(
    (transaction) =>
      transaction?.action === 'deposit' && transaction?.success && transaction?.finished_at && timeService.isTodayDate(new Date(transaction.finished_at)),
  );
}

export function buildDailyMission(params: {
  mission: any;
  isTask1Completed: boolean;
  todaySuccessfulDepositAmount: number;
  lastSuccessDepCurrency: string;
  lastSuccessDepCurrencySymbol: string;
  goalAmount: number | string;
  midGoalAmount?: number | string;
}): any {
  const goalAmount = Number(params.goalAmount);

  return {
    ...params.mission,
    isTask1Completed: params.isTask1Completed,
    isTask2Completed: params.todaySuccessfulDepositAmount >= goalAmount,
    todaySuccessfulDepositAmount: params.todaySuccessfulDepositAmount,
    percentsToGoal: Math.min(100, (params.todaySuccessfulDepositAmount / goalAmount) * 100),
    amountToGoal: Math.max(0, goalAmount - params.todaySuccessfulDepositAmount),
    lastSuccessDepCurrency: params.lastSuccessDepCurrency,
    lastSuccessDepCurrencySymbol: params.lastSuccessDepCurrencySymbol,
    goalAmount,
    ...(params.midGoalAmount != null && { midGoalAmount: Number(params.midGoalAmount) }),
  };
}
